<script setup lang="ts">
/**
 * CandidateRow — Compact horizontal candidate for mobile.
 *
 * Layout: [40px avatar] [Name + Price + YES btn] / [Party + progress bar]
 * Below 430px buttons shrink for more name/party space.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import CandidateAvatar from '@/components/CandidateAvatar.vue';
import ExchangeAvatarGroup from '@/components/ExchangeAvatarGroup.vue';
import StrikeExchangeDrawer from '@/components/StrikeExchangeDrawer.vue';
import SpeedBumpDrawer from '@/components/SpeedBumpDrawer.vue';
import { useSpeedBump } from '@/composables/useSpeedBump';
import { resolveCloseConid } from '@/utils/closeConid';
import type { ActualCandidate } from '../types/election.types';
import type { PredictionContractListContract } from '@/types/prediction.types';
import type { StrikeGroup } from '@/types/contracts.types';
import { useGetEventContractBidAsk, useGetFlexbestBidAsk } from '@/api/mdfarm.queries';
import { formatDollarPrice } from '@/utils/formatPrice';
import { useOrderTicket } from '../composables/useOrderTicket';
import { useUserState } from '@/stores/useUserState';
import { usePositions } from '@/stores/usePositions';
import { isPartyStrikeName } from '../utils/partyUtils';
import { hasPositionInPairs } from '../utils/matchedConids';

interface Props {
  candidate: ActualCandidate;
  /** Whether tapping the row navigates to single-candidate view */
  clickable?: boolean;
}

const props = withDefaults(defineProps<Props>(), { clickable: true });

const emit = defineEmits<{
  select: [conid: number];
}>();

const { t } = useI18n();
const { openOrderTicket } = useOrderTicket();
const userState = useUserState();
const positionsStore = usePositions();
const speedBump = useSpeedBump();

// A position may be held on any matched exchange, so check every yes/no conid
// carried in the candidate's conid@exchange pairs, not just the primary contract.
const hasYesPosition = computed(() => {
  return hasPositionInPairs(props.candidate.yesConidExPairs, props.candidate.conid, positionsStore.positionDataMap);
});

const hasNoPosition = computed(() => {
  return hasPositionInPairs(props.candidate.noConidExPairs, props.candidate.conid_no, positionsStore.positionDataMap);
});

const useYesFlexbest = computed(() => (props.candidate.yesConidExPairs?.length ?? 0) > 1);
const useNoFlexbest = computed(() => (props.candidate.noConidExPairs?.length ?? 0) > 1);

const yesSingleQuery = useGetEventContractBidAsk(
  computed(() => (!useYesFlexbest.value ? props.candidate.conid : undefined)),
  () => props.candidate.exchange,
);
const yesFlexbestQuery = useGetFlexbestBidAsk(
  computed(() => (useYesFlexbest.value ? props.candidate.yesConidExPairs : undefined)),
);

const noSingleQuery = useGetEventContractBidAsk(
  computed(() => (!useNoFlexbest.value ? props.candidate.conid_no : undefined)),
  () => props.candidate.exchange,
);
const noFlexbestQuery = useGetFlexbestBidAsk(
  computed(() => (useNoFlexbest.value ? props.candidate.noConidExPairs : undefined)),
);

const yesData = computed(() => (useYesFlexbest.value ? yesFlexbestQuery.data.value : yesSingleQuery.data.value));
const noData = computed(() => (useNoFlexbest.value ? noFlexbestQuery.data.value : noSingleQuery.data.value));

const candidatePrice = computed(() => ({
  price: yesData.value?.bid ?? null,
  isLoading: useYesFlexbest.value ? yesFlexbestQuery.isLoading.value : yesSingleQuery.isLoading.value,
}));

const noPrice = computed(() => ({
  price: noData.value?.bid ?? null,
  isLoading: useNoFlexbest.value ? noFlexbestQuery.isLoading.value : noSingleQuery.isLoading.value,
}));

function handleRowClick() {
  if (props.clickable) {
    emit('select', props.candidate.conid);
  }
}

const isMultiExchange = computed(() => (props.candidate.yesConidExPairs?.length ?? 0) > 1);
const hasPosition = computed(() => hasYesPosition.value || hasNoPosition.value);
const drawerVisible = ref(false);

const drawerContracts = computed<PredictionContractListContract[]>(() => {
  const yesPairs = props.candidate.yesConidExPairs ?? [];
  const noPairs = props.candidate.noConidExPairs ?? [];
  return yesPairs.map((yp, i) => {
    const [yesConid, exchange] = yp.split('@');
    const [noConid] = (noPairs[i] ?? '').split('@');
    return {
      conid_yes: Number(yesConid),
      conid_no: Number(noConid),
      exchange,
      strike_label: props.candidate.name,
      underlying_conid: props.candidate.conid,
      product_conid: props.candidate.conid,
    };
  });
});

const drawerStrikeGroups = computed<StrikeGroup[]>(() => {
  if (!drawerContracts.value.length) return [];
  return [
    {
      strike_label: props.candidate.name,
      primary: drawerContracts.value[0],
      contracts: drawerContracts.value,
      exchanges: drawerContracts.value.map((c) => c.exchange),
      expandable: drawerContracts.value.length > 1,
    },
  ];
});

function handleYesClick(e: Event) {
  e.stopPropagation();
  if (isMultiExchange.value && hasPosition.value) {
    drawerVisible.value = true;
    return;
  }
  const exchange = isMultiExchange.value ? 'BEST' : props.candidate.exchange;
  const closeConid = hasNoPosition.value
    ? exchange === 'FORECASTX'
      ? props.candidate.conid
      : props.candidate.conid_no
    : props.candidate.conid;
  openOrderTicket(closeConid, hasNoPosition.value ? 'close' : undefined, exchange, props.candidate.yesConidExPairs);
}

function handleNoClick(e: Event) {
  e.stopPropagation();
  if (isMultiExchange.value && hasPosition.value) {
    drawerVisible.value = true;
    return;
  }
  const exchange = isMultiExchange.value ? 'BEST' : props.candidate.exchange;
  const closeConid = hasYesPosition.value
    ? exchange === 'FORECASTX'
      ? props.candidate.conid_no
      : props.candidate.conid
    : props.candidate.conid_no;
  openOrderTicket(closeConid, hasYesPosition.value ? 'close' : undefined, exchange, props.candidate.noConidExPairs);
}

function handleDrawerBuyYes(contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]) {
  const group = drawerStrikeGroups.value[0];
  if (
    group &&
    exchange &&
    speedBump.intercept({ group, targetExchange: exchange, side: 'yes', conid: contract.conid_yes, eventConidExs })
  ) {
    drawerVisible.value = false;
    return;
  }
  openOrderTicket(contract.conid_yes, undefined, exchange, eventConidExs);
  drawerVisible.value = false;
}

function handleDrawerBuyNo(contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]) {
  const group = drawerStrikeGroups.value[0];
  if (
    group &&
    exchange &&
    speedBump.intercept({ group, targetExchange: exchange, side: 'no', conid: contract.conid_no, eventConidExs })
  ) {
    drawerVisible.value = false;
    return;
  }
  openOrderTicket(contract.conid_no, undefined, exchange, eventConidExs);
  drawerVisible.value = false;
}

function handleDrawerCloseYes(contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]) {
  const conid = resolveCloseConid({
    exchange,
    forecastxConid: contract.conid_yes,
    defaultConid: contract.conid_no,
  });
  openOrderTicket(conid, 'close', exchange, eventConidExs);
  drawerVisible.value = false;
}

function handleDrawerCloseNo(contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]) {
  const conid = resolveCloseConid({
    exchange,
    forecastxConid: contract.conid_no,
    defaultConid: contract.conid_yes,
  });
  openOrderTicket(conid, 'close', exchange, eventConidExs);
  drawerVisible.value = false;
}
</script>

<template>
  <div
    :class="[
      'candidate-row flex items-center gap-2.5 xs:gap-3.5 rounded-2xl border px-3 xs:px-4 py-2.5 xs:py-3.5',
      clickable && 'cursor-pointer',
      candidate.party === 'DEM' && 'border-democrat-subtle bg-democrat-subtle/20',
      candidate.party === 'GOP' && 'border-republican-subtle bg-republican-subtle/20',
      candidate.party === 'IND' && 'border-independent-subtle bg-independent-subtle/20',
      candidate.party === 'GRN' && 'border-green-subtle bg-green-subtle/20',
    ]"
    @click="handleRowClick"
  >
    <!-- Avatar -->
    <div class="relative shrink-0">
      <div
        :class="[
          'h-10 w-10 xs:h-13 xs:w-13 overflow-hidden rounded-lg xs:rounded-xl ring-1 xs:ring-2 ring-offset-1 xs:ring-offset-2',
          candidate.party === 'DEM' && 'ring-democrat-subtle',
          candidate.party === 'GOP' && 'ring-republican-subtle',
          candidate.party === 'IND' && 'ring-independent-subtle',
          candidate.party === 'GRN' && 'ring-green-subtle',
        ]"
      >
        <CandidateAvatar
          :conid="candidate.conid"
          :candidate-name="candidate.name"
          :party-code="candidate.party"
          size="fill"
        />
      </div>
      <div
        v-if="
          candidate.party === 'DEM' ||
          candidate.party === 'GOP' ||
          candidate.party === 'IND' ||
          candidate.party === 'GRN'
        "
        :class="[
          'absolute -bottom-1 -right-1.5 rounded-md px-1.5 xs:px-2 py-0.5 text-[8px] xs:text-[9px] font-black uppercase text-white shadow-md',
          candidate.party === 'DEM' && 'bg-democrat',
          candidate.party === 'GOP' && 'bg-republican',
          candidate.party === 'IND' && 'bg-independent',
          candidate.party === 'GRN' && 'bg-green',
        ]"
      >
        {{ candidate.party }}
      </div>
    </div>

    <!-- Info -->
    <div class="min-w-0 flex-1">
      <!-- Row 1: Name + Price -->
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm xs:text-base font-black leading-normal">{{ candidate.name }}</p>
          <div class="mt-0.5 flex items-center gap-2">
            <p
              v-if="!isPartyStrikeName(candidate.name)"
              :class="[
                'text-[10px] xs:text-xs font-bold uppercase tracking-wider',
                candidate.party === 'DEM' && 'text-democrat',
                candidate.party === 'GOP' && 'text-republican',
                candidate.party === 'IND' && 'text-independent',
                candidate.party === 'GRN' && 'text-green',
              ]"
            >
              {{ candidate.partyAffiliation }}
            </p>
            <ExchangeAvatarGroup v-if="candidate.exchanges" :exchanges="candidate.exchanges" size="small" />
          </div>
        </div>

        <!-- YES / NO Buttons -->
        <div class="flex shrink-0 items-center gap-1 sm:gap-1.5">
          <Button
            unstyled
            :class="[
              'cursor-pointer whitespace-nowrap rounded-lg sm:rounded-xl text-center font-black tabular-nums px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm shadow-sm min-w-[4.5rem] sm:min-w-[6.5rem] active:brightness-90',
              hasNoPosition
                ? '!bg-accent !text-on-accent hover:opacity-90'
                : '!bg-yes-subtle !text-on-yes-subtle hover:bg-(--p-positive-subtle-hover)',
            ]"
            @click="handleYesClick"
          >
            <i v-if="hasNoPosition" class="ibi-close-x-filled mr-1 text-xs align-[-.1em]" />
            <template v-else>{{ t('yes') }} </template>
            {{ candidatePrice.price === null ? '—' : `$${formatDollarPrice(candidatePrice.price)}`
            }}<i v-if="!userState.authenticated" class="ibi-lock-closed ml-1 align-[-.1em]" />
          </Button>
          <Button
            unstyled
            :class="[
              'cursor-pointer whitespace-nowrap rounded-lg sm:rounded-xl text-center font-black tabular-nums px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm shadow-sm min-w-[4.5rem] sm:min-w-[6.5rem] active:brightness-90',
              hasYesPosition
                ? '!bg-accent !text-on-accent hover:opacity-90'
                : '!bg-no-subtle !text-on-no-subtle hover:bg-(--p-negative-subtle-hover)',
            ]"
            @click="handleNoClick"
          >
            <i v-if="hasYesPosition" class="ibi-close-x-filled mr-1 text-xs align-[-.1em]" />
            <template v-else>{{ t('no') }} </template>
            {{ noPrice.price === null ? '—' : `$${formatDollarPrice(noPrice.price)}`
            }}<i v-if="!userState.authenticated" class="ibi-lock-closed ml-1 align-[-.1em]" />
          </Button>
        </div>
      </div>

      <!-- Row 2: Progress bar -->
      <div
        :class="[
          'mt-1 h-1.5 xs:h-2 w-full overflow-hidden rounded-full',
          candidate.party === 'DEM' && 'bg-democrat-subtle',
          candidate.party === 'GOP' && 'bg-republican-subtle',
          candidate.party === 'IND' && 'bg-independent-subtle',
          candidate.party === 'GRN' && 'bg-green-subtle',
        ]"
      >
        <div
          :class="[
            'h-full transition-all duration-1000',
            candidate.party === 'DEM' && 'bg-democrat',
            candidate.party === 'GOP' && 'bg-republican',
            candidate.party === 'IND' && 'bg-independent',
            candidate.party === 'GRN' && 'bg-green',
          ]"
          :style="{ width: `${candidatePrice.price === null ? 0 : candidatePrice.price * 100}%` }"
        />
      </div>
    </div>

    <!-- Exchange drawer for multi-venue with position -->
    <StrikeExchangeDrawer
      v-model="drawerVisible"
      :strike-groups="drawerStrikeGroups"
      :market-name="candidate.name"
      expiration-label=""
      price-side="bid"
      :initial-strike="candidate.name"
      @buy-yes="handleDrawerBuyYes"
      @buy-no="handleDrawerBuyNo"
      @close-yes="handleDrawerCloseYes"
      @close-no="handleDrawerCloseNo"
    />

    <SpeedBumpDrawer
      v-model:visible="speedBump.visible.value"
      :config="speedBump.config.value"
      @action="speedBump.handleAction"
    />
  </div>
</template>

<style scoped>
.candidate-row {
  -webkit-tap-highlight-color: transparent;
}

.candidate-row.cursor-pointer:active:not(:has(button:active)) {
  background-color: var(--p-surface-container);
}
</style>
